import Auth0ProviderWithHistory from '#app/shared/components/auth0/Auth0ProviderWithHistory'
import { ErrorBoundary } from '#app/shared/components/ErrorBoundary'
import Loading from '#app/shared/components/Loading/Loading'
import { TooltipProvider } from '#app/shared/components/ui/tooltip'
import { AppContext, useAppContext } from '#app/shared/contexts/AppContext'
import { useEffectAfterMount } from '#app/shared/hooks/useEffectAfterMount'
import { lazy, type PropsWithChildren, Suspense } from 'react'
import { HelmetProvider } from 'react-helmet-async'
import { AppFatalPage } from './AppFatalPage'
import { UserContext } from './AppStateStore'

// exposed for mockability
export const InternalImportAppWithLDProvider = () => import('#app/app/AppWithLDProvider')

const LazyAppWithLDProvider = lazy(() => InternalImportAppWithLDProvider())

export const AppContextProvider: React.FC<PropsWithChildren> = ({
  children
}) => {
  return <AppContext.Provider>{children}</AppContext.Provider>
}

/**
 * Performs application bootstrapping.
 * @returns
 */
export const AppBootstrapper: React.FC = () => {
  const globalService = useAppContext()
  const state = AppContext.useSelector((state) => state)

  useEffectAfterMount(() => {
    // hydrate the application after component is mounted
    globalService.send({ type: 'Hydrate' })
  }, [globalService])

  if (!state) {
    return <Loading />
  }

  if (state.matches('Failed')) {
    return <AppFatalPage />
  }

  if (!state.matches('Ready')) {
    return <Loading />
  }

  return (
    <UserContext.Provider>
      <ErrorBoundary>
        <HelmetProvider>
          <TooltipProvider>
            <Auth0ProviderWithHistory>
              <ErrorBoundary signOutOnError={true}>
                <Suspense fallback={<Loading />}>
                  <LazyAppWithLDProvider />
                </Suspense>
              </ErrorBoundary>
            </Auth0ProviderWithHistory>
          </TooltipProvider>
        </HelmetProvider>
      </ErrorBoundary>
    </UserContext.Provider>
  )
}
